import { useRef, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  Alert,
  Stack,
  Button,
  Tooltip,
  Snackbar,
  Checkbox,
  IconButton,
  Typography
} from "@material-ui/core";
import { Close, Download, InsertDriveFile, Upload } from "@material-ui/icons";
import { ALLOWED_FILE_MIME_TYPES } from "utils/contacts/bulkMessage/new";
import { motion, AnimatePresence } from "framer-motion";
import { variants } from "components/_dashboard/listings/modal/core/ListingImagesUpload";
import { preventBubbling } from "utils/listings/helper";
import { formatBytesToHumanReadableSize, getFileExtension } from "utils/listings/display";
import { Image } from "components/Image";
import { isImageFormat, isVideoFormat } from "utils/contacts/bulkMessage/helper";
import type { FilePreview } from "../../../../../@types/listings/fileData";

type BulkMessageDialogFileUploadProps = {
  snackbarOpen: boolean;
  filesPreview: FilePreview[];
  handleRemoveFile: (targetId: string) => () => void;
  handleFileUpload: ({ target: { files } }: ChangeEvent<HTMLInputElement>) => void;
  handleSnackbarClose: () => void;
};

const ACCEPTED_FILE_TYPES = ALLOWED_FILE_MIME_TYPES.join(", ");

const MotionGrid = motion(Grid);

export function BulkMessageDialogFileUpload({
  snackbarOpen,
  filesPreview,
  handleRemoveFile,
  handleFileUpload,
  handleSnackbarClose
}: BulkMessageDialogFileUploadProps): JSX.Element {
  const { t: translate } = useTranslation();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const handleClickInput = (): void => inputFileRef.current?.click();

  return (
    <Stack gap={2}>
      <input
        style={{ display: "none" }}
        ref={inputFileRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileUpload}
      />
      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: "100%" }}>
          {translate("contactsPage.error.warningFilesLimit")}
        </Alert>
      </Snackbar>
      <Button color="info" variant="contained" startIcon={<Upload />} onClick={handleClickInput}>
        {translate("contactsPage.control.uploadFiles")}
      </Button>
      {!!filesPreview?.length && (
        <Grid container spacing={2}>
          <AnimatePresence>
            {filesPreview.map((filePreview) => (
              <MotionGrid
                item
                xs={12}
                sm={6}
                md={4}
                layout
                exit={variants.exit}
                initial={variants.initial}
                animate={variants.animate}
                key={filePreview.id}
              >
                <BulkMessageFilePreviewCard
                  file={filePreview}
                  handleRemoveFile={handleRemoveFile(filePreview.id)}
                />
              </MotionGrid>
            ))}
          </AnimatePresence>
        </Grid>
      )}
    </Stack>
  );
}

type BulkMessageFilePreviewSelectProps = {
  checked: boolean;
  selectFile: () => void;
};

type BulkMessageFilePreviewCardProps = {
  file: FilePreview;
  selectProps?: BulkMessageFilePreviewSelectProps;
  handleRemoveFile?: () => void;
};

export function BulkMessageFilePreviewCard({
  file: { src, name, size },
  selectProps,
  handleRemoveFile
}: BulkMessageFilePreviewCardProps): JSX.Element {
  const { t: translate } = useTranslation();

  const handleDownload = async (): Promise<void> => {
    try {
      const response = await fetch(src);

      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = url;
      a.download = name;

      a.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const isImage = isImageFormat(name);
  const isVideo = isVideoFormat(name);

  const { checked, selectFile } = selectProps ?? {};

  return (
    <Link
      to={src}
      style={{ textDecoration: "none" }}
      rel="noopener noreferrer"
      target="_blank"
      onClick={preventBubbling()}
    >
      <Card sx={{ p: 2 }} variant="outlined">
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Stack sx={{ minWidth: 0 }} direction="row" alignItems="center" gap={1}>
            {selectProps && <Checkbox checked={checked} onClick={preventBubbling(selectFile)} />}
            {isImage ? (
              <Image
                alt={name}
                src={src}
                sx={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  borderRadius: 1,
                  objectFit: "cover",
                  bgcolor: "background.neutral"
                }}
              />
            ) : isVideo ? (
              <video
                src={src}
                style={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  borderRadius: 8,
                  objectFit: "cover",
                  backgroundColor: "rgba(0,0,0,0.1)"
                }}
              />
            ) : (
              <Box
                sx={{
                  position: "relative",
                  display: "grid",
                  placeItems: "center"
                }}
              >
                <InsertDriveFile
                  sx={{
                    width: 42,
                    height: 42
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    mt: "0.5rem",
                    color: "white",
                    fontWeight: "bold",
                    position: "absolute",
                    fontSize: "0.6rem"
                  }}
                >
                  {getFileExtension(name)}
                </Typography>
              </Box>
            )}
            <Stack sx={{ minWidth: 0 }}>
              <Typography title={name} variant="body2" noWrap>
                {name}
              </Typography>
              {!!size && (
                <Typography variant="caption">{formatBytesToHumanReadableSize(size)}</Typography>
              )}
            </Stack>
          </Stack>
          <Stack>
            {handleRemoveFile && (
              <Tooltip title={translate("contactsPage.control.delete")}>
                <IconButton size="small" color="error" onClick={preventBubbling(handleRemoveFile)}>
                  <Close />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={translate("contactsPage.control.download")}>
              <IconButton size="small" color="primary" onClick={preventBubbling(handleDownload)}>
                <Download />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Card>
    </Link>
  );
}
